import React, { useContext } from 'react';
import EditableReportSections from './constants/EditableReportSections';
import SectionBlock from 'neon-frontend-utils/src/components/Report/components/SectionBlock/SectionBlock';
import EditableText from './../../../../components/EditableText/EditableText';
import { EditableCompetencyProfile } from './components/EditableCompetencyProfile/EditableCompetencyProfile';
import { EditableAdvice } from './components/EditableAdvice/EditableAdvice';
import style from './style/editablereportsection.scss';

/**
 * EditableReportSection
 *
 * wrapper for editable report sections to support hybrid assessment, where consultants can write parts of the report
 * this relies heavily on the context provider pattern to keep all logic in one place and avoid prop drilling
 *
 * @param {Object} props - The component props.
 * @param {Object} props.i18n - The language of the report.
 * @param {Object} props.section - The section configuration.
 * @param {Object} props.classes - The style defaults.
 * @param {string} props.basedOn - The basedOn text.
 * @param {number} props.saveInterval - Determines the interval for saving text in Froala editor instances.
 * @param {Object} props.EditableReportContext - The editable report context provider.
 * @param {string} props.initials - The initials of the participant.
 * @returns {JSX.Element} The EditableReportSection component.
 */
export const EditableReportSection = props => {
    const {
        i18n,
        section,
        classes = { root: '', badge: '' },
        basedOn,
        saveInterval,
        EditableReportContext,
        initials,
        reportData
    } = props;

    const EditableReportData = useContext(EditableReportContext);

    const extractAdviceOutcome = handle => {
        const reportSection = EditableReportData?.reportData?.sections && EditableReportData.reportData.sections[handle];

        return reportSection?.scores?.find(score => score.handle === 'selectionAdviceOutcome')?.score || null;
    };

    const getEditableSection = handle => {
        switch (handle) {
            case EditableReportSections.SELECTION_ADVICE:
            case EditableReportSections.SELECTION_DEVELOPMENT_ADVICE:
            case EditableReportSections.DEVELOPMENT_ADVICE:
                return <EditableAdvice
                    saveReportText={EditableReportData.saveEditableText}
                    isFinalized={false}
                    textEditable={ EditableReportData.lockForm !== true}
                    allowedToEditReflection={true}
                    section={section}
                    saveInterval={saveInterval}
                    saveAdviceOutcome={EditableReportData.saveAdviceOutcome}
                    i18n={i18n}
                    storedAdviceOutcome={extractAdviceOutcome(section.handle)}
                    showAdviceSelection={handle === EditableReportSections.SELECTION_ADVICE || handle === EditableReportSections.SELECTION_DEVELOPMENT_ADVICE}
                />;
            case EditableReportSections.POINTS_OF_ATTENTION:
            case EditableReportSections.STRONG_POINTS:
                return <div className={style.editableTextWrapper}>
                    <EditableText
                        saveReportText={EditableReportData.saveEditableText}
                        isFinalized={false}
                        textEditable={EditableReportData.lockForm !== true}
                        allowedToEditReflection={true}
                        templateSlug={section.handle}
                        slug={section.handle}
                        name={section.handle}
                        text={section?.text || section?.autoCalculatedText}
                        saveInterval={saveInterval}
                        textareaPlaceholderText={''} // when no default or stored text available
                    />
                </div>;
            case EditableReportSections.COMPETENCY_PROFILE:
                return <EditableCompetencyProfile
                    i18n={i18n}
                    languageId={EditableReportData.languageId}
                    saveCompetency={EditableReportData.saveCompetency}
                    reportData={reportData}
                    saveInterval={saveInterval}
                    initials={initials}
                />;
            default:
                return null;
        }
    };

    return <SectionBlock
        classes={{
            root: `${style.editableReportSection} ${classes.root}`,
            badge: classes.badge
        }}>
        <span className={style.editableReportSectionSubtitle}>{basedOn}</span>
        <h3 className={style.editableReportSectionTitle}>{section.title}</h3>
        <div className={style.editableContent}>
            {getEditableSection(section.handle)}
        </div>
    </SectionBlock>;
};
